import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiMapService } from '@core/api/services/consumption/api-map.service';
import { consumptionActions } from '@store/actions/consumption.actions';
import { forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { consumptionSelectors } from '@store/selectors/consumption.selectors';
import { MapMapper } from '@core/domain/mappers/consumption/map.mapper';
import { catchError, withLatestFrom } from 'rxjs/operators';
import { masterSelectors } from '@store/selectors/master.selectors';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { ApiConsumptionRequestService } from '@core/api/services/consumption/api-consumption-request.service';
import { ConsumptionRequestMapper } from '@core/domain/mappers/consumption/consumption-request.mapper';
import { ApiAccumulatedService } from '@core/api/services/consumption/api-accumulated.service';
import { ConsumptionFilter, ConsumptionTabCode } from '@core/domain/model/consumption/consumption.model';
import { add, format, startOfDay } from 'date-fns';
import { ConsumptionMapper } from '@core/domain/mappers/consumption/consumption.mapper';
import { ApiComparativeService } from '@core/api/services/consumption/api-comparative.service';
import { filterNil } from 'ngxtension/filter-nil';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadIndicatorService } from '@shared/components/download-indicator/download-indicator.service';
import { TranslateService } from '@ngx-translate/core';
import { appActions } from '@store/actions/app.actions';
import { userSelectors } from '@store/selectors/user.selectors';

@Injectable()
export class ConsumptionEffects {
  initializeFilter$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.initTab),
      withLatestFrom(
        this._store.select(consumptionSelectors.accumulatedFilter),
        this._store.select(masterSelectors.societies),
        this._store.select(masterSelectors.installations)
      ),
      map(([, consumptionFilter, societies, installations]) => {
        const today = startOfDay(new Date());
        let filter: ConsumptionFilter;
        if (consumptionFilter == null) {
          filter = {
            startDate: add(today, { months: -1 }),
            endDate: today,
            term: 'monthly',
            societies: societies && societies.length > 0 ? [societies[0].codigo] : [],
            installations:
              societies && societies.length > 0 && installations
                ? installations.filter((value) => value.codigoRazonSocial === societies[0].codigo)
                : [],
          };
        } else {
          filter = consumptionFilter;
        }
        return consumptionActions.initFilter({ filter });
      })
    )
  );

  initializeTabs$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(consumptionActions.initTab),
      switchMap(() => this._store.select(masterSelectors.societies)),
      map((societies) =>
        societies && societies.length > 0 ? consumptionActions.changeTab({ index: 2 }) : consumptionActions.changeTab({ index: 0 })
      )
    );
  });

  changeTab$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(consumptionActions.changeTab),
      withLatestFrom(this._store.select(consumptionSelectors.accumulatedData), this._store.select(consumptionSelectors.comparativeData)),
      map(([{ index }, accumulated, comparative]) => {
        if (index === 0 && accumulated == null) {
          return consumptionActions.fetchAccumulatedData();
        }
        if (index === 1 && comparative == null) {
          return consumptionActions.fetchComparativeData();
        }
        return appActions.emptyAction();
      })
    );
  });

  fetchMapGeometry$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.fetchMapGeometry),
      map(() => this._store.selectSignal(consumptionSelectors.mapGeometry)()),
      withLatestFrom(this._store.select(userSelectors.country)),
      switchMap(([geometry, country]) => {
        if (geometry != null) {
          return of(consumptionActions.setMapGeometry({ geometry }));
        } else if (country === 'ES') {
          return this._apiMap.fetchSpainTopology().pipe(
            map((topology) => MapMapper.fromSpainProvincesTopologyToGeoJson(topology)),
            map((geometry) => consumptionActions.setMapGeometry({ geometry })),
            catchError(() => of(consumptionActions.setMapGeometryError({ error: 'CONSUMPTION.MAP.GEOMETRY_ERROR' })))
          );
        } else {
          return this._apiMap.fetchPortugalTopology().pipe(
            map((topology) => MapMapper.fromPortugalDistrictsTopologyToGeoJson(topology)),
            map((geometry) => consumptionActions.setMapGeometry({ geometry })),
            catchError(() => of(consumptionActions.setMapGeometryError({ error: 'CONSUMPTION.MAP.GEOMETRY_ERROR' })))
          );
        }
      })
    )
  );

  fetchMapCoords$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.fetchMapCoords),
      switchMap(() => {
        const coords = this._store.selectSignal(consumptionSelectors.mapCoords)();
        const installations = this._store.selectSignal(masterSelectors.installations)();
        if (coords != null && coords.length > 0) {
          return of(consumptionActions.setMapCoords({ coords }));
        } else {
          return this._apiMap.fetchInstallationCoords(installations ?? []).pipe(
            map((response) => response.data),
            map((coords) => MapMapper.fromApiCoordsToDomain(coords)),
            map((coords) => consumptionActions.setMapCoords({ coords })),
            catchError((error) => of(consumptionActions.setMapCoordsError({ error: errorFromHttpErrorResponse(error) })))
          );
        }
      })
    )
  );

  fetchRequestList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.fetchRequestList),
      switchMap(() =>
        this._apiRequest.fetchConsumptionRequest().pipe(
          map(ConsumptionRequestMapper.requestFromApiToDomain),
          map((value) => value.data),
          map((data) => consumptionActions.setRequestList({ data })),
          catchError((error) => of(consumptionActions.setRequestListError({ error: errorFromHttpErrorResponse(error) })))
        )
      )
    )
  );

  fetchAccumulated$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.fetchAccumulatedData),
      withLatestFrom(this._store.select(consumptionSelectors.accumulatedFilter).pipe(filterNil())),
      // eslint-disable-next-line consistent-return
      switchMap(([, filter]) => {
        switch (filter.term) {
          case 'daily':
            const previousDaily = {
              ...filter,
              startDate: add(filter.startDate, { days: -1 }),
              endDate: add(filter.endDate, { days: -1 }),
            };
            return forkJoin([
              this._apiAccumulated.fetchDaily(ConsumptionMapper.filterToApi(previousDaily)).pipe(catchError(() => of(null))),
              this._apiAccumulated.fetchDaily(ConsumptionMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data ? ConsumptionMapper.accumulatedDailyToDomain(previousResponse.data) : null,
                currentData: currentResponse?.data ? ConsumptionMapper.accumulatedDailyToDomain(currentResponse.data) : null,
              }))
            );
          case 'monthly':
            const previousMonthly = {
              ...filter,
              startDate: add(filter.startDate, { months: -1 }),
              endDate: add(filter.endDate, { months: -1 }),
            };
            return forkJoin([
              this._apiAccumulated.fetchMonthly(ConsumptionMapper.filterToApi(previousMonthly)).pipe(catchError(() => of(null))),
              this._apiAccumulated.fetchMonthly(ConsumptionMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data ? ConsumptionMapper.accumulatedMonthlyToDomain(previousResponse.data) : null,
                currentData: currentResponse?.data ? ConsumptionMapper.accumulatedMonthlyToDomain(currentResponse.data) : null,
              }))
            );
          case 'yearly':
            const previousYearly = {
              ...filter,
              startDate: add(filter.startDate, { years: -1 }),
              endDate: add(filter.endDate, { years: -1 }),
            };
            return forkJoin([
              this._apiAccumulated.fetchYearly(ConsumptionMapper.filterToApi(previousYearly)).pipe(catchError(() => of(null))),
              this._apiAccumulated.fetchYearly(ConsumptionMapper.filterToApi(filter)).pipe(catchError(() => of(null))),
            ]).pipe(
              map(([previousResponse, currentResponse]) => ({
                previousData: previousResponse?.data ? ConsumptionMapper.accumulatedYearlyToDomain(previousResponse.data) : null,
                currentData: currentResponse?.data ? ConsumptionMapper.accumulatedYearlyToDomain(currentResponse.data) : null,
              }))
            );
        }
      }),
      map((value) => consumptionActions.setAccumulatedData(value))
    )
  );

  downloadAccumulated$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(consumptionActions.downloadAccumulatedExcel),
        withLatestFrom(this._store.select(consumptionSelectors.accumulatedFilter).pipe(filterNil())),
        // eslint-disable-next-line consistent-return
        tap(([, filter]) => {
          switch (filter.term) {
            case 'daily':
              const dailyFilter = {
                ...filter,
                startDate: add(filter.startDate, { days: -1 }),
              };
              this._download.download(
                this._apiAccumulated.downloadExcelDaily(ConsumptionMapper.filterToApi(dailyFilter)),
                this._getExcelFileName(dailyFilter, 'accumulated')
              );
              break;
            case 'monthly':
              const monthlyFilter = {
                ...filter,
                startDate: add(filter.startDate, { months: -1 }),
              };
              this._download.download(
                this._apiAccumulated.downloadExcelMonthly(ConsumptionMapper.filterToApi(monthlyFilter)),
                this._getExcelFileName(monthlyFilter, 'accumulated')
              );
              break;
            case 'yearly':
              const yearlyFilter = {
                ...filter,
                startDate: add(filter.startDate, { years: -1 }),
              };
              this._download.download(
                this._apiAccumulated.downloadExcelYearly(ConsumptionMapper.filterToApi(yearlyFilter)),
                this._getExcelFileName(yearlyFilter, 'accumulated')
              );
              break;
          }
        })
      ),
    { dispatch: false }
  );

  fetchComparative$ = createEffect(() =>
    this._actions$.pipe(
      ofType(consumptionActions.fetchComparativeData),
      withLatestFrom(
        this._store.select(consumptionSelectors.comparativeFilter).pipe(filterNil()),
        this._store.select(masterSelectors.installations).pipe(filterNil())
      ),
      // eslint-disable-next-line consistent-return
      switchMap(([, filter, installations]) => {
        switch (filter.term) {
          case 'daily':
            return this._apiComparative
              .fetchDaily(ConsumptionMapper.filterToApi(filter))
              .pipe(catchError((err: HttpErrorResponse) => of(errorFromHttpErrorResponse(err))))
              .pipe(
                map((response) =>
                  typeof response !== 'string' ? ConsumptionMapper.comparativeDailyToDomain(response.data, installations) : response
                )
              );
          case 'monthly':
            return this._apiComparative
              .fetchMonthly(ConsumptionMapper.filterToApi(filter))
              .pipe(catchError((err: HttpErrorResponse) => of(errorFromHttpErrorResponse(err))))
              .pipe(
                map((response) =>
                  typeof response !== 'string' ? ConsumptionMapper.comparativeMonthlyToDomain(response.data, installations) : response
                )
              );
          case 'yearly':
            return this._apiComparative
              .fetchYearly(ConsumptionMapper.filterToApi(filter))
              .pipe(catchError((err: HttpErrorResponse) => of(errorFromHttpErrorResponse(err))))
              .pipe(
                map((response) =>
                  typeof response !== 'string' ? ConsumptionMapper.comparativeYearlyToDomain(response.data, installations) : response
                )
              );
        }
      }),
      map((data) =>
        typeof data !== 'string' ? consumptionActions.setComparativeData({ data }) : consumptionActions.setComparativeError({ error: data })
      )
    )
  );

  downloadComparative$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(consumptionActions.downloadComparativeExcel),
        withLatestFrom(this._store.select(consumptionSelectors.comparativeFilter).pipe(filterNil())),
        // eslint-disable-next-line consistent-return
        tap(([, filter]) => {
          const filename = this._getExcelFileName(filter, 'comparative');
          switch (filter.term) {
            case 'daily':
              this._download.download(this._apiComparative.downloadExcelDaily(ConsumptionMapper.filterToApi(filter)), filename);
              break;
            case 'monthly':
              this._download.download(this._apiComparative.downloadExcelMonthly(ConsumptionMapper.filterToApi(filter)), filename);
              break;
            case 'yearly':
              this._download.download(this._apiComparative.downloadExcelYearly(ConsumptionMapper.filterToApi(filter)), filename);
              break;
          }
        })
      ),
    { dispatch: false }
  );

  private _getExcelFileName(filter: ConsumptionFilter, currentTab: ConsumptionTabCode): string {
    const startDate = format(filter.startDate, 'dd-MM-yyyy');
    const endDate = format(filter.endDate, 'dd-MM-yyyy');
    const tabTranslation = this._translate.instant(`CONSUMPTION.TABS.${currentTab.toUpperCase()}`);
    const termTranslation = this._translate.instant(`GENERAL.${filter.term.toUpperCase()}`);
    return `${tabTranslation}_${termTranslation}_${startDate}_${endDate}`;
  }

  constructor(
    private readonly _actions$: Actions,
    private readonly _apiMap: ApiMapService,
    private readonly _apiRequest: ApiConsumptionRequestService,
    private readonly _apiAccumulated: ApiAccumulatedService,
    private readonly _apiComparative: ApiComparativeService,
    private readonly _store: Store<StoreState>,
    private readonly _download: DownloadIndicatorService,
    private readonly _translate: TranslateService
  ) {}
}
